// src/pages/Community/Location/LocationDetail.js
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { doc, onSnapshot, collection } from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { db } from "../../../firebase";
import PageWrapper from "../../../components/PageWrapper";
import "../css/LocationDetail.css";
import LocationEditModal from "../Location/LocationEditModal";
import LocationDetailContent from "./LocationDetailContent";

/* Minimal inline Feedback modal (swap with your shared <FeedbackModal /> if you have one) */
function LocationFeedbackModal({ open, onClose, locationDoc }) {
  const [text, setText] = useState("");

  if (!open) return null;
  return (
    <div className="locd-modal" role="dialog" aria-modal="true">
      <div className="locd-modal-card">
        <div className="locd-modal-head">
          <strong>Feedback for: {locationDoc?.title || "Location"}</strong>
          <button className="locd-action" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <textarea
          className="locd-modal-text"
          rows={6}
          placeholder="Share a correction, hint, or improvement…"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <div className="locd-modal-actions">
          <button className="locd-action" onClick={onClose}>
            Cancel
          </button>
          <button
            className="locd-action"
            onClick={() => {
              navigator.clipboard?.writeText(text).catch(() => { });
              setText("");
              onClose();
            }}
          >
            💬 Submit
          </button>
        </div>
      </div>
    </div>
  );
}

export default function LocationDetail() {
  const { id } = useParams();
  const location = useLocation();

  // --- state & hooks (always declared in same order) ---
  const [authUser, setAuthUser] = useState(null);
  const uid = authUser?.uid ?? null;
  const [editOpen, setEditOpen] = useState(false);
  const [loc, setLoc] = useState(undefined); // undefined = loading, null = not found
  const [linkCopied, setLinkCopied] = useState(false);
  const [fbOpen, setFbOpen] = useState(false);

  // Immediate UI feedback for selected reaction (best-effort)
  const [selectedReactionFromBar, setSelectedReactionFromBar] = useState(null);

  // basePath/bookmarkKey MUST be declared unconditionally
  const basePath = useMemo(() => ["locations", id], [id]);

  // owner check (can use loc safely)
  const isOwner = !!uid && (loc?.createdBy === uid || loc?.authorUid === uid);

  // auth listener
  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, (u) => setAuthUser(u || null));
    return () => unsub();
  }, []);

  // Firestore doc listener
  useEffect(() => {
    if (!id) return;
    const ref = doc(db, "locations", id);
    const unsub = onSnapshot(ref, (snap) => {
      setLoc(snap.exists() ? { id: snap.id, ...snap.data() } : null);
    });
    return () => unsub();
  }, [id]);

  // share / copy
  const onShare = useCallback(async () => {
    const url = window.location.origin + location.pathname;
    try {
      if (navigator.share) {
        await navigator.share({ title: loc?.title ?? "Location", url });
        return;
      }
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 1800);
    } catch {
      // no-op
    }
  }, [loc?.title, location.pathname]);

  // Loading / Not found states
  if (loc === undefined) {
    return (
      <PageWrapper>
        <div className="locd-wrap">
          <div className="locd-top">
            <Link className="locd-back" to="/map-locations">
              ← Back to Locations
            </Link>
          </div>
          <div className="locd-skel">
            <div className="locd-skel-cover" />
            <div className="locd-skel-lines" />
          </div>
        </div>
      </PageWrapper>
    );
  }

  const canEdit =
    !!uid &&
    (loc.createdBy === uid ||
      (Array.isArray(loc.allowEditBy) && loc.allowEditBy.includes(uid)) ||
      loc.isAdminEditable === true);

  if (loc === null) {
    return (
      <PageWrapper>
        <div className="locd-wrap">
          <div className="locd-top">
            <Link className="locd-back" to="/map-locations">
              ← Back to Locations
            </Link>
          </div>
          <div className="locd-empty">Location not found.</div>
        </div>
      </PageWrapper>
    );
  }


  return (
    <PageWrapper>
      <div className="locd-wrap">

        {/* Top bar */}
        <div className="locd-detail-toprow">
          <Link className="locd-back" to="/map-locations">
            ← Back to Locations
          </Link>

          <div className="locd-actions">
            <button className="locd-action" onClick={onShare}>
              {linkCopied ? "✓ Link copied" : "⤴ Share"}
            </button>

            <button
              className="locd-action"
              onClick={() => setFbOpen(true)}
            >
              💬 Feedback
            </button>

            {canEdit && (
              <button
                className="locd-action"
                onClick={() => setEditOpen(true)}
              >
                ✎ Edit
              </button>
            )}
          </div>

        </div>

        {/* ✅ SHARED CONTENT */}
        <LocationDetailContent
          loc={loc}
          uid={uid}
          isOwner={isOwner}
          presenceKey="locations"
          locationId={loc.id}
          basePath={basePath}
          featuredReaction={null}
          selectedReactionFromBar={selectedReactionFromBar}
          onReactionSelect={setSelectedReactionFromBar}
          renderHeader
          renderEngagement
          renderDiscussion
        />

        {/* Modals */}
        <LocationFeedbackModal
          open={fbOpen}
          onClose={() => setFbOpen(false)}
          locationDoc={loc}
        />

        <LocationEditModal
          open={editOpen}
          onClose={() => setEditOpen(false)}
          locationId={loc.id}
          user={authUser}
          role={authUser?.role}
        />

      </div>
    </PageWrapper>

  );
}
